/**
 * Which DANI chat a crosstab came from (M4S-5777 X150, decision L2).
 *
 * `ai_layout.state.daniOrigin` is record state carried by the browser, like `surveyScope`: the
 * workspace sends it with every save, stamped with the record's own key, and the save route keeps
 * it only when that key is the key being saved, so a Duplicate (a new key) drops the link (L3).
 * It is informational only: the crosstab links back to its conversation, and `receiptId` names the
 * server's log line for the plan it came from. Nothing authorizes on it.
 *
 * Pure.
 */

export interface DaniOrigin {
    v: 1;
    /** The record the link belongs to; a save under any other key drops it. */
    layoutKey: string;
    /** The DANI conversation, or null when the plan came from a chat with no id. */
    conversationId: string | null;
    /** The short id of the server's signed receipt for the plan (`receiptIdOf`). */
    receiptId: string | null;
    /** When the plan was opened in Crosstabs. */
    at: string;
}

const KEY_RE = /^[A-Za-z0-9_-]{1,64}$/;
const CONV_RE = /^[A-Za-z0-9_-]{1,200}$/;
const RECEIPT_RE = /^[0-9a-f]{16}$/;

/** The one read of a stored or sent origin, on the save route's write and on every open. */
export function parseDaniOrigin(value: unknown): DaniOrigin | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const v = value as Record<string, unknown>;
    if (v.v !== 1) return null;
    const layoutKey = typeof v.layoutKey === 'string' ? v.layoutKey.trim().toLowerCase() : '';
    if (!KEY_RE.test(layoutKey)) return null;
    const conversationId =
        typeof v.conversationId === 'string' && CONV_RE.test(v.conversationId)
            ? v.conversationId
            : null;
    const receiptId =
        typeof v.receiptId === 'string' && RECEIPT_RE.test(v.receiptId) ? v.receiptId : null;
    const at = typeof v.at === 'string' && !Number.isNaN(Date.parse(v.at)) ? v.at : null;
    if (!at) return null;
    // Canonical key order: the autosave signature compares JSON.
    return { v: 1, layoutKey, conversationId, receiptId, at };
}

/** The origin when it belongs to this record; null for another key (a Duplicate) or none. */
export function daniOriginFor(value: unknown, layoutKey: string | null): DaniOrigin | null {
    const origin = parseDaniOrigin(value);
    if (!origin || !layoutKey) return null;
    return origin.layoutKey === layoutKey.trim().toLowerCase() ? origin : null;
}

/** Where the link back goes: the DANI conversation, or nothing when the plan had none. */
export function daniConversationHref(origin: DaniOrigin | null): string | null {
    return origin?.conversationId ? `/dani/${encodeURIComponent(origin.conversationId)}` : null;
}
